import React, { useEffect, useRef, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { locations } from '../../data/locations';
import { mapsConfig } from '../../config/maps';
import { useMaps } from '../../contexts/MapsContext';
import MapCluster from './MapCluster';
import { createInfoWindow } from '../../utils/maps';

// Define Texas bounds
const TEXAS_BOUNDS = {
  north: 36.5007,  // Northern border of Texas
  south: 25.8371,  // Southern border of Texas
  west: -106.6452, // Western border of Texas
  east: -93.5083   // Eastern border of Texas
};

const TEXAS_CENTER = { lat: 31.9686, lng: -99.9018 }; // Center of Texas

interface MapProps {
  onError: (error: string) => void;
  center?: google.maps.LatLngLiteral | null;
  'aria-hidden'?: boolean;
}

export default function Map({ onError, center, 'aria-hidden': ariaHidden }: MapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const { isLoaded } = useMaps();

  const handleMarkerClick = useCallback((location: Location) => {
    if (!mapInstanceRef.current || !infoWindowRef.current) return;
    
    const content = createInfoWindow(location);
    infoWindowRef.current.setContent(content);
    infoWindowRef.current.setPosition(location.coordinates);
    infoWindowRef.current.open(mapInstanceRef.current);
  }, []);

  const initializeMap = useCallback(async () => {
    if (!mapRef.current || !isLoaded) return;

    try {
      const bounds = new google.maps.LatLngBounds();
      locations.forEach(location => bounds.extend(location.coordinates));
      
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center: center || TEXAS_CENTER,
        zoom: center ? 13 : 12,
        styles: mapsConfig.styles,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER
        },
        restriction: {
          latLngBounds: TEXAS_BOUNDS,
          strictBounds: false
        }
      });

      if (!center) {
        mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
      }

      infoWindowRef.current = new google.maps.InfoWindow();
    } catch (error) {
      console.error('Map initialization error:', error);
      onError('Failed to load Google Maps. Please try again later.');
    }
  }, [onError, center, isLoaded]);

  useEffect(() => {
    initializeMap();

    return () => {
    };
  }, [initializeMap]);

  useEffect(() => {
    if (center && mapInstanceRef.current) {
      mapInstanceRef.current.panTo(center);
      mapInstanceRef.current.setZoom(13);
    }
  }, [center]);

  return (
    <div 
      ref={mapRef} 
      className="w-full h-full rounded-lg shadow-lg"
      aria-hidden={ariaHidden}
      role="application"
      aria-label="Interactive map of BTM locations"
    >
      {mapInstanceRef.current && <MapCluster map={mapInstanceRef.current} locations={locations} onMarkerClick={handleMarkerClick} />}
    </div>
  );
}